import dayjs from "dayjs";


import { apiFetch } from "../api/apiFetch";

export function formatEventDateTimeForCheckIn(event) {
  if (!event) return "";
  const start = event.start_datetime ? dayjs(event.start_datetime) : null;
  const end = event.end_datetime ? dayjs(event.end_datetime) : null;
  if (!start) return "";
  const dateStr = start.format("MMM D, YYYY");
  const timeStr = end ? `${start.format("h:mm A")} – ${end.format("h:mm A")}` : start.format("h:mm A");
  return `${dateStr} · ${timeStr}`;
}

/** Same calendar-day rule as the QR scanner for marking attended (missing start date does not block). */
export function getEventCheckInDateGate(event) {
  const eventDate = event?.start_datetime ? dayjs(event.start_datetime).format("YYYY-MM-DD") : "";
  const todayDate = dayjs().format("YYYY-MM-DD");
  if (!eventDate) {
    return { ok: true };
  }
  if (eventDate !== todayDate) {
    const eventDateDisplay = event?.start_datetime ? dayjs(event.start_datetime).format("MMM D, YYYY") : eventDate;
    const isPast = eventDate < todayDate;
    const message = isPast
      ? `Past event. Check-in is only allowed on the event date. This event was on ${eventDateDisplay}.`
      : `Future event. Check-in is only allowed on the event date. This event is on ${eventDateDisplay}.`;
    return { ok: false, message };
  }
  return { ok: true };
}

export function isCapacityMaxedForCheckIn(metrics) {
  const maxCap = metrics?.max_capacity;
  const attendedCt = metrics?.attended_count;
  const maxNum = maxCap != null && Number(maxCap) > 0 ? Math.floor(Number(maxCap)) : null;
  const attendedNum = Number.isFinite(Number(attendedCt)) ? Number(attendedCt) : 0;
  return maxNum != null && attendedNum >= maxNum;
}

function countRegisteredPendingCheckIn(participantsList) {
  const rows = Array.isArray(participantsList) ? participantsList : [];
  let n = 0;
  for (const p of rows) {
    if ((p?.attendance || "").trim() === "Registered") n += 1;
  }
  return n;
}

function getWaitlistBlockedByRegisteredMessage(registeredPendingCount) {
  const n = Number.isFinite(Number(registeredPendingCount)) ? Math.max(0, Math.floor(Number(registeredPendingCount))) : 0;
  if (n === 1) {
    return "Waitlisted check-in is not available because 1 registered guest is still pending check-in. Check in registered guests first, then allow waitlist check-in.";
  }
  return `Waitlisted check-in is not available because ${n} registered guest(s) are still pending check-in. Check in registered guests first, then allow waitlist check-in.`;
}

export async function fetchEventParticipants(serverUrl, accountId, eventId) {
  const eid = eventId != null && String(eventId).trim() !== "" ? String(eventId).trim() : null;
  if (!eid || !accountId) return { ok: false, participants: [], error: "Event not found." };
  const resp = await apiFetch(`${serverUrl}/getEventParticipants`, {
    method: "POST",
    body: JSON.stringify({ account_id: accountId, event_id: eid }),
  });
  if (!resp.ok) {
    const errData = await resp.json().catch(() => ({}));
    return { ok: false, participants: [], error: errData.error || "Failed to load participants." };
  }
  const data = await resp.json().catch(() => []);
  return { ok: true, participants: Array.isArray(data) ? data : [], error: null };
}

export async function ensureWaitlistCheckInAllowed({ serverUrl, accountId, eventId }) {
  const fetched = await fetchEventParticipants(serverUrl, accountId, eventId);
  if (!fetched.ok) return { ok: false, message: fetched.error || "Unable to validate waitlist check-in." };
  const registeredPending = countRegisteredPendingCheckIn(fetched.participants);
  return registeredPending > 0
    ? { ok: false, message: getWaitlistBlockedByRegisteredMessage(registeredPending), registeredPending }
    : { ok: true, message: "", registeredPending: 0 };
}

export async function requestMarkParticipantAttended(serverUrl, accountId, participantId, allowWaitlistCheckin) {
  const body = { account_id: accountId, participant_id: participantId };
  if (allowWaitlistCheckin) body.allow_waitlist_checkin = true;
  const markRes = await apiFetch(`${serverUrl}/markParticipantAttended`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!markRes.ok) {
    const errData = await markRes.json().catch(() => ({}));
    return { ok: false, error: errData.error || "Failed to mark checked in." };
  }
  const markData = await markRes.json().catch(() => ({}));
  return { ok: true, registration_metrics: markData.registration_metrics };
}

export async function reloadParticipantsAfterMark(eventOrEventId, loadParticipantsFn) {
  const eid =
    eventOrEventId && typeof eventOrEventId === "object" && eventOrEventId._id != null
      ? String(eventOrEventId._id)
      : eventOrEventId != null && String(eventOrEventId).trim() !== ""
        ? String(eventOrEventId).trim()
        : null;
  if (eid && typeof loadParticipantsFn === "function") {
    await loadParticipantsFn(eid);
  }
}

/**
 * Bracketed name for QR scan confirmation copy, e.g. " [Jane Doe]".
 */
export function formatCheckInParticipantNameBracket(participantName) {
  const name = participantName != null ? String(participantName).trim() : "";
  return name ? ` [${name}]` : "";
}

/**
 * Preflight via getParticipantWithEvent — same rules for QR scan, Potential list, and capacity bump.
 * @param {{ fromEventParticipant?: boolean }} [options]
 */
export async function evaluateParticipantCheckInPreflight(serverUrl, accountId, id, options = {}) {
  const fromRegistration = options.fromEventParticipant === true;
  const trimmed = id != null && String(id).trim() !== "" ? String(id).trim() : null;
  if (!trimmed || !accountId) {
    return { kind: "error", message: "Participant not found." };
  }
  const body = fromRegistration
    ? { account_id: accountId, event_participant_id: trimmed }
    : { account_id: accountId, user_id: trimmed };
  const getRes = await apiFetch(`${serverUrl}/getParticipantWithEvent`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!getRes.ok) {
    const errData = await getRes.json().catch(() => ({}));
    const msg = errData.error || (await getRes.text()) || "Participant not found.";
    const participantName =
      errData.participantName != null && String(errData.participantName).trim() !== ""
        ? String(errData.participantName).trim()
        : undefined;
    return { kind: "error", message: msg, participantName };
  }
  const data = await getRes.json();
  if (data.needs_event_choice && Array.isArray(data.choices) && data.choices.length > 0) {
    return { kind: "pick_event", choices: data.choices };
  }
  const { participant, event, registration_metrics } = data;
  const registrationRowId = String(participant._id);
  const participantName = [participant.first_name, participant.last_name].filter(Boolean).join(" ").trim() || "Unknown";
  const eventName = event && event.name ? String(event.name).trim() : "Unknown event";
  const eventDateTime = formatEventDateTimeForCheckIn(event);
  const photography_consent = participant.photography_consent;
  const checkInContext = {
    participantName,
    eventName,
    eventDateTime,
    registration_metrics,
    photography_consent,
  };

  const dateGate = getEventCheckInDateGate(event);
  if (!dateGate.ok) {
    return { kind: "blocked", event, result: { ...checkInContext, error: dateGate.message } };
  }
  const attendanceNow = (participant.attendance || "").trim();
  if (attendanceNow === "Attended" || attendanceNow === "Walk-In") {
    return { kind: "blocked", event, result: { ...checkInContext, status: "already_scanned" } };
  }
  if (isCapacityMaxedForCheckIn(registration_metrics)) {
    return {
      kind: "blocked",
      event,
      result: {
        ...checkInContext,
        status: "capacity_maxed",
        participant_id: registrationRowId,
        event,
      },
    };
  }
  if ((participant.attendance || "").trim() === "Waitlisted") {
    return {
      kind: "waitlist",
      event,
      waitlistConfirm: {
        participant_id: registrationRowId,
        event_id: event && event._id ? String(event._id) : "",
        ...checkInContext,
      },
    };
  }
  return {
    kind: "mark",
    event,
    participant_id: registrationRowId,
    ...checkInContext,
  };
}

function buildAttendedResult(pre, registration_metrics) {
  return {
    participantName: pre.participantName,
    eventName: pre.eventName,
    eventDateTime: pre.eventDateTime,
    status: "attended",
    registration_metrics: registration_metrics || pre.registration_metrics,
    photography_consent: pre.photography_consent,
  };
}

function buildMarkErrorResult(pre, error) {
  return {
    participantName: pre.participantName,
    eventName: pre.eventName,
    eventDateTime: pre.eventDateTime,
    status: "error",
    error,
    registration_metrics: pre.registration_metrics,
    photography_consent: pre.photography_consent,
  };
}

/** Mark attended and show the standard check-in result modal. */
export async function markAttendedFromPreflight(pre, ctx, { allowWaitlistCheckin = false } = {}) {
  const { serverUrl, accountId, setResult, loadParticipantsFn } = ctx;
  setResult({
    participantName: pre.participantName,
    eventName: pre.eventName,
    eventDateTime: pre.eventDateTime,
    status: "loading",
    registration_metrics: pre.registration_metrics,
    photography_consent: pre.photography_consent,
  });
  const marked = await requestMarkParticipantAttended(
    serverUrl,
    accountId,
    pre.participant_id,
    allowWaitlistCheckin
  );
  if (!marked.ok) {
    setResult(buildMarkErrorResult(pre, marked.error));
    return { ok: false };
  }
  await reloadParticipantsAfterMark(pre.event, loadParticipantsFn);
  setResult(buildAttendedResult(pre, marked.registration_metrics));
  return { ok: true };
}

/**
 * Single check-in continuation after preflight (QR scan, Potential list, capacity bump).
 * @returns {Promise<{ done: boolean, manualWaitlist?: { waitlistConfirm: object, resumeQueue: string[] } }>}
 */
export async function continueCheckInFromPreflight(pre, ctx) {
  const {
    serverUrl,
    accountId,
    getSelectedEventId,
    setSelectedEventId,
    setResult,
    setWaitlistConfirm,
    manualBatch,
  } = ctx;

  const failBatch = () => {
    if (typeof manualBatch?.onBatchFail === "function") manualBatch.onBatchFail();
  };

  if (pre.kind === "error") {
    failBatch();
    setResult({
      status: "error",
      error: pre.message,
      title: "Check-in not completed",
      ...(pre.participantName ? { participantName: pre.participantName } : {}),
    });
    return { done: true };
  }

  if (pre.event && pre.event._id) {
    setSelectedEventId(String(pre.event._id));
  }

  if (pre.kind === "blocked") {
    failBatch();
    if (pre.result?.status === "capacity_maxed" && pre.event) {
      setResult({ ...pre.result, event: pre.event });
    } else {
      setResult(pre.result);
    }
    return { done: true };
  }

  if (pre.kind === "waitlist") {
    const eid =
      pre.waitlistConfirm?.event_id ||
      (pre.event && pre.event._id ? String(pre.event._id) : "") ||
      (typeof getSelectedEventId === "function" ? getSelectedEventId() : "") ||
      "";
    const allow = await ensureWaitlistCheckInAllowed({ serverUrl, accountId, eventId: eid });
    if (!allow.ok) {
      failBatch();
      setResult({
        title: "Waitlisted participant",
        participantName: pre.waitlistConfirm?.participantName,
        eventName: pre.waitlistConfirm?.eventName,
        eventDateTime: pre.waitlistConfirm?.eventDateTime,
        status: "error",
        error: allow.message,
        registration_metrics: pre.waitlistConfirm?.registration_metrics,
        photography_consent: pre.waitlistConfirm?.photography_consent,
      });
      return { done: true };
    }
    if (manualBatch) {
      return {
        done: false,
        manualWaitlist: {
          waitlistConfirm: pre.waitlistConfirm,
          resumeQueue: manualBatch.resumeQueue || [],
        },
      };
    }
    setWaitlistConfirm(pre.waitlistConfirm);
    return { done: true };
  }

  if (pre.kind === "mark") {
    const marked = await markAttendedFromPreflight(pre, ctx, { allowWaitlistCheckin: false });
    if (!marked.ok) {
      failBatch();
      return { done: true };
    }
    if (typeof manualBatch?.onMarkSuccess === "function") {
      manualBatch.onMarkSuccess(manualBatch.resumeQueue || []);
    }
    return { done: true };
  }

  return { done: true };
}

/** Waitlist confirm → same mark path as QR / Pending Check-In. */
export async function completeWaitlistAllowCheckIn(waitlistConfirm, ctx, { manualBatch } = {}) {
  const pre = {
    participant_id: waitlistConfirm.participant_id,
    participantName: waitlistConfirm.participantName,
    eventName: waitlistConfirm.eventName,
    eventDateTime: waitlistConfirm.eventDateTime,
    registration_metrics: waitlistConfirm.registration_metrics,
    photography_consent: waitlistConfirm.photography_consent,
    event: waitlistConfirm.event_id ? { _id: waitlistConfirm.event_id } : undefined,
  };
  const marked = await markAttendedFromPreflight(pre, ctx, { allowWaitlistCheckin: true });
  if (!marked.ok) {
    if (typeof manualBatch?.onBatchFail === "function") manualBatch.onBatchFail();
    return { ok: false };
  }
  if (typeof manualBatch?.onMarkSuccess === "function") {
    manualBatch.onMarkSuccess(manualBatch.resumeQueue || []);
  }
  return { ok: true };
}
